<template>
  <PageShell :background="background">
    <PageHeader size="page" align="start" :title="t('community.title')" :lead="t('community.lead')">
      <template #actions>
        <LandingButton variant="accent" :action="openDiscord">{{ t('community.joinDiscord') }}</LandingButton>
      </template>
    </PageHeader>

    <PageSection :title="t('community.channelsTitle')" :lead="t('community.channelsLead')">
      <div class="groups">
        <Card v-for="group in groups" :key="group.role">
          <h3 class="group__title">{{ t(`community.roles.${group.role}.title`) }}</h3>
          <p class="group__note">{{ t(`community.roles.${group.role}.note`) }}</p>
          <ul class="group__links">
            <li v-for="link in group.links" :key="link.name">
              <a :href="link.url" target="_blank" rel="noopener noreferrer">
                <i :class="link.icon" aria-hidden="true"></i>
                {{ link.name }}
              </a>
            </li>
          </ul>
        </Card>
      </div>
    </PageSection>

    <PageSection :title="t('community.supportTitle')">
      <Prose>
        <p>{{ t('community.supportBody') }}</p>
      </Prose>
      <div>
        <LandingButton variant="ghost" :action="openEmail">{{ t('community.emailCta') }}</LandingButton>
      </div>
    </PageSection>
  </PageShell>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import PageShell from '@/components/public/PageShell.vue';
import PageHeader from '@/components/public/PageHeader.vue';
import PageSection from '@/components/public/PageSection.vue';
import Prose from '@/components/public/Prose.vue';
import Card from '@/components/public/Card.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
import { CONTACT_EMAIL, linksByRole } from '@/data/socialLinks';
import background from '@/assets/images/backgrounds/common/city-night.webp';

const { t } = useI18n();

const groups = ['play', 'follow', 'support'].map((role) => ({ role, links: linksByRole(role) }));

const openDiscord = () => window.open(linksByRole('play')[0].url, '_blank', 'noopener');
const openEmail = () => {
  window.location.href = `mailto:${CONTACT_EMAIL}`;
};
</script>

<style scoped>
.groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(16rem, 100%), 1fr));
  gap: var(--space-4);
}

.group__title {
  font-family: var(--font-body);
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  letter-spacing: 0;
  margin-bottom: var(--space-2);
}

.group__note {
  color: var(--text-muted);
  font-size: var(--text-sm);
  margin-bottom: var(--space-4);
}

.group__links {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/*
 * Nine accent-coloured links in three columns, directly under an
 * accent-filled Discord button, meant the page's one action had nothing to
 * distinguish it from a list of outbound links. They read as text; the accent
 * comes back on hover, where it means "this is live" rather than "this is
 * important".
 */
.group__links a {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text);
  font-size: var(--text-sm);
}

.group__links a:hover {
  color: var(--accent);
}

.group__links i {
  color: var(--text-faint);
  width: 1.1em;
  text-align: center;
  transition: color var(--duration) var(--ease);
}

.group__links a:hover i {
  color: var(--accent);
}
</style>
